import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from 'react';
import { periodSetters, type PeriodFilterValue } from './period-filter';
import type { PeriodType } from './period-math';

/**
 * 기간 필터 상태 코어(발전량·수익 공용 shape). 화면별 addon(예: 발전량 `inverterId`)은 이 훅을 감싸 `setFilters`
 * 로 확장한다. 세터 4종은 공용 `periodSetters`(날짜기준 cascade·range clamp) spread.
 */
export interface PeriodFilterState<T extends PeriodFilterValue> {
  /** 적용된 필터(SSOT) — 쿼리·칩·집계 소스. */
  filters: T;
  /** 상태 write seam — addon 세터(예: setInverterId)가 functional update 로 확장할 때만. */
  setFilters: Dispatch<SetStateAction<T>>;
  /** 필터 적용(칩 제거 등 통째 교체). */
  apply: (next: T) => void;
  /** 전체 초기화 — `makeDefault()` 로. */
  reset: () => void;
  /** Applied≠기본값 — [초기화] 노출 판정. */
  isDirty: boolean;
  /** 날짜기준(일/월/연/기간) 변경 — 기간을 그 단위 기본값으로 cascade 리셋. */
  setPeriod: (period: PeriodType) => void;
  /** 단일 기준일(일/월/연) 변경. */
  setDate: (date: string) => void;
  /** range 시작일 — 역전·최대 span 클램프. */
  setStartDate: (startDate: string) => void;
  /** range 종료일 — 역전·최대 span 클램프. */
  setEndDate: (endDate: string) => void;
}

/**
 * 기간 필터 상태 훅(발전량·수익 공용) — 페이지 로컬(useState) SSOT + apply/reset/isDirty + `periodSetters` spread.
 * **live**(즉시반영). `makeDefault`·`equal` 은 모듈 레벨 안정 함수(feature 가 제공). 화면별 추가 세터는 반환 `setFilters`
 * 로 이 훅을 감싸 확장(예: `useGenerationFilters` = 이 코어 + `setInverterId`). URL sync 는 후속(이 훅만 교체).
 */
export function usePeriodFilterState<T extends PeriodFilterValue>(
  makeDefault: () => T,
  equal: (a: T, b: T) => boolean,
): PeriodFilterState<T> {
  const [filters, setFilters] = useState<T>(makeDefault);

  const apply = useCallback((next: T) => setFilters(next), []);
  const reset = useCallback(() => setFilters(makeDefault()), [makeDefault]);
  const isDirty = useMemo(() => !equal(filters, makeDefault()), [filters, equal, makeDefault]);
  const period = useMemo(() => periodSetters<T>(setFilters), []);

  return { filters, setFilters, apply, reset, isDirty, ...period };
}
